import React from "react";
import { Link } from "react-router-dom";

import { Logo } from "@/components/custom/logo";
import { apex25LegalUrl } from "@/lib/apex25AppUrl";

type FooterColumn = {
  title: string;
  links: Array<{ label: string; href: string; external?: boolean }>;
};

const columns: FooterColumn[] = [
  {
    title: "Product",
    links: [
      { label: "Semantic Search", href: "/#semantic-search" },
      { label: "Spotlight Intros", href: "/#spotlight-intros" },
      { label: "Bulk AI Interviews", href: "/#bulk-interviews" },
      { label: "Citation Evidence", href: "/#citation-ai" },
    ],
  },
  {
    title: "Workflow",
    links: [
      { label: "How It Works", href: "/#workflow" },
      { label: "Pricing", href: "/pricing" },
      { label: "Our Approach", href: "/#testimonials" },
      { label: "FAQ", href: "/#faq" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "The Problem", href: "/#crisis" },
      { label: "Blog", href: "/blog" },
      { label: "Changelog", href: "/changelog" },
      { label: "Contact", href: "/contact" },
    ],
  },
  {
    title: "For Candidates",
    links: [
      { label: "First Round", href: "https://firstround.com.au", external: true },
      { label: "Request a Demo", href: "mailto:apex25@theresume.io?subject=Demo Request", external: true },
      { label: "Talk to Sales", href: "mailto:apex25@theresume.io", external: true },
      { label: "Get Support", href: "mailto:apex25@theresume.io", external: true },
    ],
  },
];

// theRecruit shares its legal documents with Apex 25: they live on tryapex25.com (the in-app copy
// under /apex25 on staging and localhost). "/legal" is the hub.
const legalLinks = [
  { label: "Terms of Service", path: "/terms" },
  { label: "Privacy Policy", path: "/privacy" },
  { label: "Candidate terms", path: "/candidate-terms" },
  { label: "All legal documents", path: "/legal" },
];

const FooterLink = ({ href, label, external }: { href: string; label: string; external?: boolean }) => {
  const className = "text-sm transition-colors text-[rgba(250,249,244,0.30)] hover:text-[#d4a853]";

  if (external) {
    // Open real web destinations in a new tab; mailto: links should stay in-page so the
    // user's mail client takes over without leaving an empty about:blank behind.
    const isWebUrl = /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        className={className}
        {...(isWebUrl ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  );
};

export const MarketingFooter: React.FC = () => {
  return (
    <footer
      className="marketing-footer border-t pt-20 pb-12 relative overflow-hidden"
      style={{
        background: "linear-gradient(to bottom, #04031a, #03030e, #010108)",
        borderColor: "rgba(255,255,255,0.06)",
      }}
    >
      <div
        className="marketing-footer__top-accent absolute top-0 inset-x-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(212,168,83,0.40), transparent)" }}
      />
      <div
        className="absolute top-0 left-1/4 w-[500px] h-[400px] rounded-full blur-[120px] pointer-events-none opacity-40"
        style={{ background: "rgba(123,88,4,0.08)" }}
      />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] rounded-full blur-[120px] pointer-events-none opacity-30"
        style={{ background: "rgba(212,168,83,0.06)" }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-16">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <Logo theme="dark" size={40} />
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "rgba(250,249,244,0.35)" }}>
              Intelligence-first recruiting for modern teams.
            </p>
          </div>

          {columns.map((column) => (
            <div key={column.title}>
              <p
                className="text-xs font-bold uppercase tracking-widest mb-5"
                style={{ color: "rgba(250,249,244,0.50)" }}
              >
                {column.title}
              </p>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <FooterLink {...link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="flex flex-col md:flex-row items-center justify-between pt-8"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <p className="text-sm mb-4 md:mb-0" style={{ color: "rgba(250,249,244,0.25)" }}>
            © 2026 theRecruit AI Inc. Crafted with precision.
          </p>
          <nav aria-label="Legal" className="mb-4 md:mb-0">
            <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <FooterLink label={link.label} href={apex25LegalUrl(link.path)} external />
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex items-center gap-1.5" style={{ color: "rgba(250,249,244,0.30)" }}>
            <span className="text-[#d4a853]">●</span>
            <span className="text-sm">Intelligence-first recruiting for modern teams</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
